import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Scrollbars } from 'react-custom-scrollbars'
import { isEqual } from 'lodash-es'

import Filter from './Filter/Filter'

import {
  addNewSetting,
  removeLastSetting,
  updateLastSetting,
  updateSettingsIds
} from '../../../store/actions/settings.actions'
import { getLastFiltersSetting } from '../../../helpers/util'
import { sendMetric } from '../../../helpers/metric'

import { mapControls } from '../../../constants/controls'
import { withTranslation } from 'react-i18next'

import './FiltersControl.scss'

const defaultValues = {
  grayscale: 0,
  dogpatch: 0,
  aden: 0,
  hudson: 0,
  perpetua: 0
}

class FiltersControl extends PureComponent {
  static propTypes = {
    imageData: PropTypes.object.isRequired,
    settingsList: PropTypes.array.isRequired,
    addNewSetting: PropTypes.func.isRequired,
    updateLastSetting: PropTypes.func.isRequired,
    dispatchRemoveLastSetting: PropTypes.func.isRequired,
    currentSettingId: PropTypes.number.isRequired,
    updateSettingsIds: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired
  }

  state = {
    activeFilterId: 0
  }

  constructor(props) {
    super(props)

    const { imageData } = props

    this.canvas = document.createElement('canvas')
    const ctx = this.canvas.getContext('2d')

    this.canvas.width = imageData.width
    this.canvas.height = imageData.height

    ctx.putImageData(imageData, 0, 0)
  }

  componentDidUpdate(prevProps) {
    const { currentSettingId } = this.props
    const { activeFilterId } = this.state

    // when user clicks UNDO while there is an active filter, deactivate current filter
    if (currentSettingId < prevProps.currentSettingId && activeFilterId) {
      this.setState({ activeFilterId: 0 })
    }
  }

  onChange = (value, name, filterId) => {
    const { settingsList, currentSettingId, updateLastSetting } = this.props
    const lastSetting = getLastFiltersSetting(settingsList, currentSettingId)

    if (lastSetting && lastSetting.id === currentSettingId && lastSetting.filterId === filterId) {
      updateLastSetting({
        ...lastSetting,
        values: {
          ...lastSetting.values,
          [name]: value
        },
      })
    }
  }

  onApply = (name) => {
    const {
      settingsList,
      currentSettingId,
      updateLastSetting,
      dispatchRemoveLastSetting
    } = this.props

    const lastSetting = getLastFiltersSetting(settingsList, currentSettingId)
    const preLastSetting = lastSetting ? getLastFiltersSetting(settingsList, lastSetting.id - 1) : undefined

    // if values haven't changed - remove current setting from the settingsList
    // in order not to have successive duplicate entries
    if (lastSetting && preLastSetting && isEqual(lastSetting.values, preLastSetting.values)) {
      dispatchRemoveLastSetting()
    } else {
      updateLastSetting({
        ...lastSetting,
        isCommitted: true
      })
    }

    this.setState({
      activeFilterId: 0
    })

    const item = mapControls.filters.filter((item) => item.name === name)[0]

    sendMetric({
      event: 'FILTER_APPLIED',
      gtm: {
        eventCategory: 'Interactions',
        eventAction: 'click',
        eventLabel: 'FILTER_APPLIED'
      },
      amplitude: {
        PRESET_NAME: item.label
      }
    })
  }

  onActivate = (name, filterId) => {
    const {
      addNewSetting,
      updateLastSetting,
      settingsList,
      currentSettingId,
      updateSettingsIds
    } = this.props

    const lastSetting = getLastFiltersSetting(settingsList, currentSettingId)
    const preLastSetting = lastSetting ? getLastFiltersSetting(settingsList, lastSetting.id - 1) : undefined

    if (lastSetting && !lastSetting.isCommitted) {
      // if there is an active filter replace it and use the previous filter values
      updateLastSetting({
        name: 'filters',
        id: currentSettingId,
        filterId,
        values: {
          ...(preLastSetting && preLastSetting.values),
          [name]: (preLastSetting && preLastSetting.values[name]) || 100
        },
      })
    } else {
      updateSettingsIds(currentSettingId + 1)
      addNewSetting({
        name: 'filters',
        id: currentSettingId + 1,
        filterId,
        isCommitted: false,
        values: {
          ...(lastSetting && lastSetting.values),
          [name]: (lastSetting && lastSetting.values[name]) || 100
        },
      })
    }

    this.setState({ activeFilterId : filterId })
  }

  onDeactivate = (name, filterId) => {
    const {
      settingsList,
      currentSettingId,
      dispatchRemoveLastSetting,
      updateLastSetting
    } = this.props
    const lastSetting = getLastFiltersSetting(settingsList, currentSettingId)
    const preLastSetting = lastSetting ? getLastFiltersSetting(settingsList, lastSetting.id - 1) : undefined

    this.setState({ activeFilterId: 0})

    // if the filter was previously applied - reset it
    if (preLastSetting && preLastSetting.values[name]) {
      updateLastSetting({
        name: 'filters',
        id: currentSettingId,
        filterId,
        isCommitted: true,
        values: {
          ...(preLastSetting && preLastSetting.values),
          [name]: 0
        },
      })

      const item = mapControls.filters.filter((item) => item.name === name)[0]

      sendMetric({
        event: 'FILTER_RESETED',
        gtm: {
          eventCategory: 'Interactions',
          eventAction: 'click',
          eventLabel: 'FILTER_RESETED'
        },
        amplitude: {
          PRESET_NAME: item.label
        }
      })
    } else {
      dispatchRemoveLastSetting()
    }
  }

  render() {
    const { activeFilterId } = this.state
    const { 
      settingsList, 
      currentSettingId, 
      t
    } = this.props
    const [grayscale, dogpatch, aden, hudson, perpetua] = mapControls.filters

    const lastSetting = getLastFiltersSetting(settingsList, currentSettingId)
    const values = Object.assign({}, defaultValues, lastSetting && lastSetting.values)

    return (
      <div className="filters__control">
        <Scrollbars
          style={{ height: '100%' }}
          renderThumbVertical={(props) => <div {...props} className="thumb-vertical" />}
        >
          <div className="filters__content">
            <div className="filters__control-title">{t('titles.effects')}</div>
            <Filter
              value={values.grayscale}
              type={grayscale}
              isActive={activeFilterId === grayscale.id}
              onApply={this.onApply}
              onActivate={this.onActivate}
              onDeactivate={this.onDeactivate}
              onChange={this.onChange}
              bg={this.canvas}
            />
            <Filter
              value={values.dogpatch}
              type={dogpatch}
              isActive={activeFilterId === dogpatch.id}
              onApply={this.onApply}
              onActivate={this.onActivate}
              onDeactivate={this.onDeactivate}
              onChange={this.onChange}
              bg={this.canvas}
            />
            <Filter
              value={values.aden}
              type={aden}
              isActive={activeFilterId === aden.id}
              onApply={this.onApply}
              onActivate={this.onActivate}
              onDeactivate={this.onDeactivate}
              onChange={this.onChange}
              bg={this.canvas}
            />
            <Filter
              value={values.hudson}
              type={hudson}
              isActive={activeFilterId === hudson.id}
              onApply={this.onApply}
              onActivate={this.onActivate}
              onDeactivate={this.onDeactivate}
              onChange={this.onChange}
              bg={this.canvas}
            />
            <Filter
              value={values.perpetua}
              type={perpetua}
              isActive={activeFilterId === perpetua.id}
              onApply={this.onApply}
              onActivate={this.onActivate}
              onDeactivate={this.onDeactivate}
              onChange={this.onChange}
              bg={this.canvas}
            />
          </div>
        </Scrollbars>
      </div>
    )
  }
}

const mapStateToProps = ({
  settingsStore: { settingsList, currentSettingId },
  imageStore: { imageData }
}) => ({
  settingsList,
  currentSettingId,
  imageData
})

const mapDispatchToProps = (dispatch) => ({
  addNewSetting: (setting) => dispatch(addNewSetting(setting)),
  updateLastSetting: (setting) => dispatch(updateLastSetting(setting)),
  updateSettingsIds: (id) => dispatch(updateSettingsIds(id)),
  dispatchRemoveLastSetting: (id) => dispatch(removeLastSetting(id)),
})

export default withTranslation()(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(FiltersControl)
)
